import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import echo from '../config/echo';

/**
 * Private channel: manager.{cmId}
 * Event: .issued.coupon.updated
 * Payload: { action: 'created'|'used'|'deleted', data: {...} }
 */
export function useIssuedCouponSocket(onEvent) {
  const user = useSelector((state) => state.auth.user);
  const cmId = user?.cmId;
  const accountType = user?.cmAccountType;
  const callbackRef = useRef(onEvent);
  callbackRef.current = onEvent;

  useEffect(() => {
    // Admin listens to the global channel so they see all clients' changes.
    const channel =
      accountType === 'ioyou'
        ? echo.channel('issued-coupon-channel')
        : cmId
          ? echo.private(`manager.${cmId}`)
          : null;
    if (!channel) return;

    const handler = (e) => callbackRef.current(e);
    channel.listen('.issued.coupon.updated', handler);

    return () => {
      channel.stopListening('.issued.coupon.updated', handler);
      if (accountType === 'ioyou') echo.leave('issued-coupon-channel');
      else if (cmId) echo.leave(`manager.${cmId}`);
    };
  }, [accountType, cmId]);
}

/**
 * Public channel: coupon-type-channel
 * Event: .coupon.type.updated
 * Payload: { action: 'created'|'updated'|'deleted', couponType: {...} }
 */
export function useCouponTypeSocket(onEvent) {
  const callbackRef = useRef(onEvent);
  callbackRef.current = onEvent;

  useEffect(() => {
    const channel = echo.channel('coupon-type-channel');
    const handler = (e) => callbackRef.current(e);
    channel.listen('.coupon.type.updated', handler);

    return () => {
      channel.stopListening('.coupon.type.updated', handler);
      echo.leave('coupon-type-channel');
    };
  }, []);
}

/**
 * Public channel: admin-client-channel
 * Event: .client.updated
 * Payload: { action: 'created'|'blocked'|'unblocked'|'deleted', clientData: {...} }
 */
export function useClientSocket(onEvent) {
  const callbackRef = useRef(onEvent);
  callbackRef.current = onEvent;

  useEffect(() => {
    const channel = echo.channel('admin-client-channel');
    const handler = (e) => callbackRef.current(e);
    channel.listen('.client.updated', handler);

    return () => {
      channel.stopListening('.client.updated', handler);
      echo.leave('admin-client-channel');
    };
  }, []);
}
